import { useState, useEffect, useRef, useCallback, useId } from 'react';
import Icon from "../icons/Icon";
import Modal from '../modals/Modal';
import { useAuth } from '../../hooks/useAuth';
import { db } from '../../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useToast } from '../../contexts/ToastContext';
import {
  CSV_FIELDS,
  CSV_FIELD_MAP,
  MAX_TEMPLATES,
  getDefaultColumns,
  getUsableFields,
} from '../../constants/csvFieldDefinitions';
import '../../styles/components/csv-export-layout.css';

const CsvExportLayoutPage = () => {
  const { company, hasPermission } = useAuth();
  const canEdit = hasPermission('set:csv:edit');
  const toast = useToast();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const deleteTitleId = useId();

  // Editor state
  const [editorMode, setEditorMode] = useState(null); // null | 'add' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [columns, setColumns] = useState([]);
  const [preserveLeadingZeros, setPreserveLeadingZeros] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Drag state
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // ─── Firestore ───

  const docRef = company ? doc(db, 'companies', company.id, 'settings', 'csvTemplates') : null;

  const fetchTemplates = useCallback(async () => {
    if (!docRef) return;
    try {
      setLoading(true);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        setTemplates(snap.data().templates || []);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      console.error('[CsvExportLayout] fetch error:', err);
      toast.error('Failed to load CSV templates');
    } finally {
      setLoading(false);
    }
  }, [docRef]);

  useEffect(() => {
    if (company) fetchTemplates();
  }, [company]);

  const saveTemplates = async (updatedTemplates) => {
    if (!docRef) return;
    setSaving(true);
    try {
      await setDoc(docRef, { templates: updatedTemplates }, { merge: true });
      setTemplates(updatedTemplates);
    } catch (err) {
      console.error('[CsvExportLayout] save error:', err);
      toast.error('Failed to save templates');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // ─── Template List Actions ───

  const openAddEditor = () => {
    setEditorMode('add');
    setEditingId(null);
    setTemplateName('');
    setColumns(getDefaultColumns());
    setPreserveLeadingZeros(true);
  };

  const openEditEditor = (template) => {
    setEditorMode('edit');
    setEditingId(template.id);
    setTemplateName(template.name);
    // Ensure all known fields are present (handles fields added after template creation)
    // and drop ids whose definition has since been retired (e.g. lateDeduction,
    // BL-007) — the list below skips unknown ids, so keeping them would inflate
    // the column counter with a row the user can't untick, and they'd persist
    // back to Firestore on every save.
    const known = template.columns.filter(c => CSV_FIELD_MAP[c.id]);
    const existingIds = new Set(known.map(c => c.id));
    const merged = [
      ...known,
      ...CSV_FIELDS.filter(f => !existingIds.has(f.id)).map(f => ({ id: f.id, enabled: false })),
    ];
    setColumns(merged);
    setPreserveLeadingZeros(template.preserveLeadingZeros ?? false);
  };

  const closeEditor = () => {
    setEditorMode(null);
    setEditingId(null);
    setTemplateName('');
    setColumns([]);
    setPreserveLeadingZeros(false);
  };

  const handleSetDefault = async (templateId) => {
    const updated = templates.map(t => ({
      ...t,
      isDefault: t.id === templateId,
    }));
    try {
      await saveTemplates(updated);
      toast.success('Default template updated');
    } catch {
      // error already toasted
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const updated = templates.filter(t => t.id !== deleteTarget.id);
    // If deleted template was default, make first remaining template default
    if (deleteTarget.isDefault && updated.length > 0) {
      updated[0].isDefault = true;
    }
    try {
      await saveTemplates(updated);
      toast.success(`"${deleteTarget.name}" deleted`);
    } catch {
      // error already toasted
    }
    setDeleteTarget(null);
  };

  // ─── Editor Save ───

  const handleSave = async () => {
    const trimmedName = templateName.trim();
    if (!trimmedName) {
      toast.error('Template name is required');
      return;
    }

    if (trimmedName.length > 30) {
      toast.error('Template name must be 30 characters or less');
      return;
    }

    if (!/^[a-zA-Z0-9 ]+$/.test(trimmedName)) {
      toast.error('Template name can only contain letters, numbers, and spaces');
      return;
    }

    // Duplicate name check (case-insensitive, exclude current template when editing)
    const isDuplicate = templates.some(
      t => t.name.toLowerCase() === trimmedName.toLowerCase() && t.id !== editingId
    );
    if (isDuplicate) {
      toast.error('A template with this name already exists');
      return;
    }

    const enabledCount = columns.filter(c => c.enabled).length;
    if (enabledCount === 0) {
      toast.error('At least one column must be enabled');
      return;
    }

    const now = new Date().toISOString();
    let updated;

    if (editorMode === 'add') {
      // Backend limit check
      if (templates.length >= MAX_TEMPLATES) {
        toast.error(`Maximum of ${MAX_TEMPLATES} CSV templates allowed`);
        return;
      }

      const newTemplate = {
        id: `tpl_${Date.now()}`,
        name: trimmedName,
        isDefault: templates.length === 0, // first template auto-becomes default
        columns,
        preserveLeadingZeros,
        createdAt: now,
        updatedAt: now,
      };
      updated = [...templates, newTemplate];
    } else {
      updated = templates.map(t =>
        t.id === editingId
          ? { ...t, name: trimmedName, columns, preserveLeadingZeros, updatedAt: now }
          : t
      );
    }

    try {
      await saveTemplates(updated);
      toast.success(editorMode === 'add' ? 'Template created' : 'Template saved');
      closeEditor();
    } catch {
      // error already toasted
    }
  };

  // ─── Column Toggle ───

  const toggleColumn = (index) => {
    setColumns(prev => {
      const next = [...prev];
      next[index] = { ...next[index], enabled: !next[index].enabled };
      return next;
    });
  };

  // ─── Drag & Drop ───

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const from = dragItem.current;
    const to = dragOverItem.current;
    if (from !== to) {
      setColumns(prev => {
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
    }
    dragItem.current = null;
    dragOverItem.current = null;
    setDragOverIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // ─── Helpers ───

  const getTemplateSummary = (template) => {
    // Same source as the export and the download modal, so the three can't drift
    // apart on a retired column id (BL-007/BL-084).
    const fields = getUsableFields(template);
    return `${fields.length} columns \u00b7 ${fields.map(f => f.label).join(', ')}`;
  };

  const enabledCount = columns.filter(c => c.enabled).length;
  const totalCount = columns.length;
  const atLimit = templates.length >= MAX_TEMPLATES;

  // ─── Render: Loading ───

  if (loading) {
    return (
      <div>
        <h1 className="csv-page-title">CSV Export Layout</h1>
        <p className="csv-page-subtitle">Create and manage CSV export templates for payroll data</p>
        <div className="csv-loading">
          <div className="csv-loading__spinner"></div>
          Loading templates...
        </div>
      </div>
    );
  }

  // ─── Render: Editor View ───

  if (editorMode) {
    return (
      <div>
        <div className="csv-editor-header">
          <button className="csv-editor-back" onClick={closeEditor} title="Back to list">
            <Icon name="chevron-left" size={16} />
          </button>
          <span className="csv-editor-title">
            {editorMode === 'add' ? 'New Template' : 'Edit Template'}
          </span>
        </div>

        {/* Template Name */}
        <div className="csv-card" style={{ marginBottom: 16 }}>
          <div className="csv-card-body" style={{ padding: '16px 24px' }}>
            <label className="csv-name-label">Template Name</label>
            <input
              type="text"
              className="csv-name-input"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., Bank Transfer, Full Report"
              maxLength={30}
            />
          </div>
        </div>

        {/* Column Editor */}
        <div className="csv-card">
          <div className="csv-card-header">
            <div className="csv-card-header-left">
              <div className="csv-card-icon">
                <Icon name="list" size={20} />
              </div>
              <div>
                <div className="csv-card-title">Columns</div>
                <div className="csv-card-description">Drag to reorder, toggle to include or exclude</div>
              </div>
            </div>
          </div>

          <div className="csv-card-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <div className="csv-column-count" style={{ margin: 0 }}>{enabledCount} of {totalCount} columns enabled</div>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ui-text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                title={'Wraps account numbers, mobile numbers and employee IDs as ="000590227114" so Excel preserves leading zeros.'}
              >
                <input
                  type="checkbox"
                  checked={preserveLeadingZeros}
                  onChange={(e) => setPreserveLeadingZeros(e.target.checked)}
                />
                Preserve leading zeros (for Excel)
                <Icon name="info" size={14} style={{ color: 'var(--ui-text-faint)', flexShrink: 0 }} />
              </label>
            </div>
            <ul className="csv-column-list">
              {columns.map((col, index) => {
                const def = CSV_FIELD_MAP[col.id];
                if (!def) return null;
                return (
                  <li
                    key={col.id}
                    className={`csv-column-item${col.enabled ? '' : ' disabled'}${dragOverIndex === index ? ' drag-over' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                  >
                    <span className="csv-drag-handle">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
                        <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
                        <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
                      </svg>
                    </span>
                    <span className="csv-column-name">{def.label}</span>
                    <span className="csv-column-field">{def.field}</span>
                    <button
                      className={`csv-toggle${col.enabled ? ' active' : ''}`}
                      onClick={() => toggleColumn(index)}
                      type="button"
                    />
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="csv-card-footer">
            <button className="csv-btn-cancel" onClick={closeEditor}>Cancel</button>
            <button
              className="csv-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Template List View ───

  return (
    <div>
      <h1 className="csv-page-title">CSV Export Layout</h1>
      <p className="csv-page-subtitle">Create and manage CSV export templates for payroll data</p>

      <div className="csv-card">
        <div className="csv-card-header">
          <div className="csv-card-header-left">
            <div className="csv-card-icon">
              <Icon name="file-text" size={20} />
            </div>
            <div>
              <div className="csv-card-title">Saved Templates</div>
              <div className="csv-card-description">
                {templates.length} template{templates.length !== 1 ? 's' : ''} configured
              </div>
            </div>
          </div>
          {canEdit && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                className="csv-btn-add"
                onClick={openAddEditor}
                disabled={atLimit}
                title={atLimit ? `Maximum ${MAX_TEMPLATES} templates reached` : 'Add a new template'}
              >
                <Icon name="plus" size={14} />
                Add Template
              </button>
              {atLimit && <span className="csv-limit-text">Limit reached ({MAX_TEMPLATES})</span>}
            </div>
          )}
        </div>

        <div className="csv-card-body">
          {templates.length === 0 ? (
            <div className="csv-empty-state">
              <div className="csv-empty-state__icon"><Icon name="file-text" size={32} /></div>
              <div className="csv-empty-state__title">No templates yet</div>
              <div className="csv-empty-state__text">
                {canEdit ? 'Create your first CSV export template to get started.' : 'No CSV export templates have been configured.'}
              </div>
              {canEdit && (
                <button className="csv-btn-primary" onClick={openAddEditor}>
                  Create Template
                </button>
              )}
            </div>
          ) : (
            <ul className="csv-template-list">
              {templates.map((tpl) => (
                <li key={tpl.id} className="csv-template-item">
                  <div className="csv-template-item-left">
                    <div className="csv-template-icon">
                      <Icon name="file-text" />
                    </div>
                    <div className="csv-template-info">
                      <div className="csv-template-name">
                        {tpl.name}
                        {tpl.isDefault && <span className="csv-template-default-badge">Default</span>}
                      </div>
                      <div className="csv-template-meta">{getTemplateSummary(tpl)}</div>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="csv-template-item-right">
                      {!tpl.isDefault && (
                        <button
                          className="csv-template-action-btn set-default"
                          onClick={() => handleSetDefault(tpl.id)}
                          title="Set as default"
                        >
                          <Icon name="star" size={12} />
                          Set Default
                        </button>
                      )}
                      <button
                        className="csv-template-action-btn"
                        onClick={() => openEditEditor(tpl)}
                        title="Edit"
                      >
                        <Icon name="pencil" size={14} />
                      </button>
                      <button
                        className="csv-template-action-btn delete"
                        onClick={() => setDeleteTarget(tpl)}
                        title="Delete"
                      >
                        <Icon name="trash-2" size={14} />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Info box */}
      <div className="csv-info-box">
        <span className="csv-info-box__icon">
          <Icon name="info" size={18} />
        </span>
        <span className="csv-info-box__text">
          Templates define which columns appear when exporting payroll data from the Payroll List page. The <strong>default</strong> template is used automatically. You can select a different template when downloading.
        </span>
      </div>

      {/* Delete Confirmation Modal */}
      {canEdit && deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} busy={saving} labelledBy={deleteTitleId} className="csv-delete-modal">
            <div className="csv-delete-modal__title" id={deleteTitleId}>Delete Template</div>
            <div className="csv-delete-modal__text">
              Are you sure you want to delete "{deleteTarget.name}"? This action cannot be undone.
            </div>
            <div className="csv-delete-modal__actions">
              <button className="csv-btn-cancel" onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</button>
              <button className="csv-btn-danger" onClick={handleDeleteConfirm} disabled={saving}>
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default CsvExportLayoutPage;
